import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useAuth } from './AuthContext';

interface WebSocketContextType {
  isConnected: boolean;
  status: 'connecting' | 'connected' | 'disconnected' | 'reconnecting';
  sendMessage: (data: any) => void;
  subscribe: (channel: string) => void;
}

const WebSocketContext = createContext<WebSocketContextType | undefined>(undefined);

export const WebSocketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [status, setStatus] = useState<'connecting' | 'connected' | 'disconnected' | 'reconnecting'>('disconnected');
  const ws = useRef<WebSocket | null>(null);
  const reconnectTimeout = useRef<NodeJS.Timeout>();
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 10; // Daha uzun süre denemeye devam etmesi için artırıldı

  const connectRef = useRef<() => void>();

  const connect = useCallback(() => {
    if (!user) {return;}

    // WebSocket desteği aktif - production ve local'de çalışacak
    // Hono backend /ws endpoint'ine bağlanır

    // Production'da Cloudflare Worker URL'ini, local'de 8888 portunu kullanır
    const host = import.meta.env.VITE_WS_URL || (import.meta.env.VITE_API_URL?.replace(/^http/, 'ws') || 'ws://localhost:8888');
    const token = localStorage.getItem('token') || '';
    const wsUrl = `${host}/ws?user_id=${user.id}&token=${encodeURIComponent(token)}`;

    console.log(`[WS] Attempting connection to ${wsUrl}`);
    setStatus(prev => prev === 'disconnected' ? 'connecting' : 'reconnecting');

    ws.current = new WebSocket(wsUrl);

    ws.current.onopen = () => {
      console.log('%c[WS] Connected Successfully', 'color: #10b981; font-weight: bold');
      setStatus('connected');
      reconnectAttempts.current = 0;
    };

    ws.current.onclose = (event) => {
      console.log(`%c[WS] Connection Closed: ${event.reason || 'No reason'}`, 'color: #ef4444');
      setStatus('disconnected');

      // Gemini Logic: Otomatik Retry (Exponential Backoff)
      if (reconnectAttempts.current < maxReconnectAttempts) {
        // İlk deneme 1sn, sonra 2sn, 4sn... Max 30sn
        const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
        console.log(`[WS] Reconnect attempt ${reconnectAttempts.current + 1}/${maxReconnectAttempts} in ${delay}ms...`);

        reconnectTimeout.current = setTimeout(() => {
          reconnectAttempts.current += 1;
          if (connectRef.current) { connectRef.current(); }
        }, delay);
      } else {
        console.error('[WS] Maximum reconnect attempts reached.');
      }
    };

    ws.current.onerror = (error) => {
      console.error('[WS] Socket Error:', error);
      ws.current?.close();
    };

    ws.current.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data as string);
        console.log('[WS] Received:', data);

        // Twin curiosity questions → dispatch to chat
        if (data.type === 'user_chat_update' && data.is_curiosity) {
          window.dispatchEvent(new CustomEvent('runai:twin-message', {
            detail: {
              content: data.content,
              agentName: 'Twin',
              agentId: data.agent_id,
            }
          }))
        }

        // Agent action broadcast from backend (SAVE_FILE, etc.)
        if (data.type === 'agent_action') {
          if (data.action === 'code_inject' && data.filename && data.content) {
            // Store in localStorage for RunCode to pick up (handles race condition)
            try {
              localStorage.setItem('runai_pending_inject', JSON.stringify({
                filename: data.filename,
                content: data.content,
                ts: Date.now()
              }))
            } catch { /* */ }
            // Also dispatch directly
            window.dispatchEvent(new CustomEvent('runai:code-inject', {
              detail: { filename: data.filename, content: data.content }
            }))
          }

          // Open window
          if (data.action === 'open_window' && data.window_id) {
            window.dispatchEvent(new CustomEvent('runai:open-window', { detail: data.window_id }))
            // If opening ask-ai with ticket context, pre-fill support tab
            if (data.window_id === 'ask-ai' && data.ticket_issue) {
              setTimeout(() => {
                window.dispatchEvent(new CustomEvent('runai:askai-open-ticket', {
                  detail: { issue: data.ticket_issue }
                }))
              }, 500)
            }
          }

          // Browser navigate
          if (data.action === 'browser_navigate' && data.url) {
            // Open RunBrowser first, then navigate after mount delay
            window.dispatchEvent(new CustomEvent('runai:open-window', { detail: 'run-browser' }))
            setTimeout(() => {
              window.dispatchEvent(new CustomEvent('runai:browser-navigate', { detail: { url: data.url } }))
            }, 400)
          }

          // Browser summarize
          if (data.action === 'browser_summarize') {
            window.dispatchEvent(new CustomEvent('runai:browser-summarize', {}))
          }

          // Browser translate
          if (data.action === 'browser_translate' && data.lang) {
            window.dispatchEvent(new CustomEvent('runai:browser-translate', { detail: { lang: data.lang } }))
          }

          // Browser download
          if (data.action === 'browser_download' && data.url) {
            window.dispatchEvent(new CustomEvent('runai:browser-download', { detail: { url: data.url } }))
          }

          // Browser AI action (WEB_CLICK, WEB_FILL, WEB_SCRAPE, etc.)
          if (data.action === 'browser_ai_action') {
            window.dispatchEvent(new CustomEvent('runai:browser-ai-action', {
              detail: {
                browser_action: data.browser_action,
                selector: data.selector,
                value: data.value,
                target: data.target,
                title: data.title,
              }
            }))
          }

          // Browser new tab
          if (data.action === 'browser_new_tab' && data.url) {
            window.dispatchEvent(new CustomEvent('runai:open-window', { detail: 'run-browser' }))
            setTimeout(() => {
              window.dispatchEvent(new CustomEvent('runai:browser-new-tab', { detail: { url: data.url } }))
            }, 400)
          }
        }

        // OS action from backend
        if (data.type === 'os_action' && data.action) {
          window.dispatchEvent(new CustomEvent(`runai:${data.action}`, { detail: data.payload }))
        }

      } catch (e) {
        console.error('[WS] Message parse error:', e);
      }
    };
  }, [user]);

  useEffect(() => {
    connectRef.current = connect;
  }, [connect]);

  useEffect(() => {
    if (!user) return;
    // Zaten bağlıysa tekrar bağlanma
    if (ws.current && ws.current.readyState === WebSocket.OPEN) return;
    connect();
    return () => {
      if (reconnectTimeout.current) {clearTimeout(reconnectTimeout.current);}
      if (ws.current) {
        ws.current.onclose = null; // reconnect loop'u engelle
        ws.current.close();
      }
    };
  }, [user?.id]); // sadece user.id değişince yeniden bağlan

  const sendMessage = useCallback((data: any) => {
    if (ws.current?.readyState === WebSocket.OPEN) {
      ws.current.send(JSON.stringify(data));
    } else {
      console.warn('[WS] Socket is not open. Current state:', ws.current?.readyState);
    }
  }, []);

  const subscribe = useCallback((channel: string) => {
    sendMessage({ type: 'subscribe', channel });
  }, [sendMessage]);

  return (
    <WebSocketContext.Provider value={{ isConnected: status === 'connected', status, sendMessage, subscribe }}>
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (context === undefined) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};
